import demoSongStats from '../data/demoSongStats.json';
import type { Song } from '../types/domain';

export interface DemoSongStatOverride {
  creatorName?: string;
  playCount?: number;
  loveCount?: number;
  saveCount?: number;
  shareCount?: number;
}

const overrides = demoSongStats.songs as Readonly<Record<string, DemoSongStatOverride>>;

export type SimulatedDayType = 'good' | 'normal' | 'below-normal' | 'bad';

export const SIMULATED_DAILY_PLAY_RANGES: Readonly<
  Record<SimulatedDayType, readonly [minimum: number, maximum: number]>
> = {
  good: [60, 100],
  normal: [12, 35],
  'below-normal': [3, 12],
  bad: [0, 5],
};

const DAY_MS = 24 * 60 * 60 * 1_000;
const GROWTH_BUCKETS_PER_DAY = 96;
const GROWTH_BUCKET_MS = DAY_MS / GROWTH_BUCKETS_PER_DAY;
const GOOD_DAY_CHANCE = 0.0158;
const REPEAT_GOOD_DAY_CHANCE = 0.0037;
const GOOD_DAY_COOLDOWN_DAYS = 14;
const FIRST_WEEK_DAYS = 7;
// Existing public songs without a publication timestamp begin growing from this rollout.
const SIMULATION_ROLLOUT_AT = Date.UTC(2026, 9, 5);

function flagEnabled(value: string | undefined): boolean {
  return value === '1' || value?.toLowerCase() === 'true' || value?.toLowerCase() === 'yes';
}

function readProcessEnvFlag(): string | undefined {
  if (typeof process === 'undefined') return undefined;
  return process.env.LYRIXA_DEMO_STATS ?? process.env.VITE_LYRIXA_DEMO_STATS;
}

export function demoSongStatsEnabled(): boolean {
  const processValue = readProcessEnvFlag();
  if (processValue !== undefined) return flagEnabled(processValue);
  const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return flagEnabled(viteEnv?.VITE_LYRIXA_DEMO_STATS);
}

function stableHash(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  // Avalanche similar seeds such as adjacent day and bucket numbers before sampling ranges.
  hash += hash << 13;
  hash ^= hash >>> 7;
  hash += hash << 3;
  hash ^= hash >>> 17;
  hash += hash << 5;
  return hash >>> 0;
}

function integerInRange(seed: string, minimum: number, maximum: number): number {
  return minimum + (stableHash(seed) % (maximum - minimum + 1));
}

function ratioInRange(seed: string, minimum: number, maximum: number): number {
  return minimum + (stableHash(seed) / 0xffffffff) * (maximum - minimum);
}

function simulatedDayType(songId: string, dayIndex: number, cooldownDays: number): SimulatedDayType {
  const roll = ratioInRange(`${songId}:day:${dayIndex}:type`, 0, 1);
  const goodChance = cooldownDays > 0 ? REPEAT_GOOD_DAY_CHANCE : GOOD_DAY_CHANCE;
  if (roll < goodChance) return 'good';
  if (roll < (cooldownDays > 0 ? 0.55 : 0.6)) return 'normal';
  if (roll < (cooldownDays > 0 ? 0.88 : 0.9)) return 'below-normal';
  return 'bad';
}

/** Stable day sequence used by both the projection and probability regression tests. */
export function simulatedDayTypesFor(songId: string, days: number): SimulatedDayType[] {
  const types: SimulatedDayType[] = [];
  let cooldownDays = 0;
  for (let dayIndex = 0; dayIndex < Math.max(0, Math.floor(days)); dayIndex += 1) {
    const type = simulatedDayType(songId, dayIndex, cooldownDays);
    types.push(type);
    if (type === 'good') cooldownDays = GOOD_DAY_COOLDOWN_DAYS;
    else if (cooldownDays > 0) cooldownDays -= 1;
  }
  return types;
}

function dailyPlayTotal(songId: string, dayIndex: number, type: SimulatedDayType): number {
  const [minimum, maximum] = SIMULATED_DAILY_PLAY_RANGES[type];
  const base = integerInRange(`${songId}:day:${dayIndex}:plays`, minimum, maximum);
  if (dayIndex >= FIRST_WEEK_DAYS || type === 'good' || base === 0) return base;
  const boost = ratioInRange(`${songId}:first-week-boost`, 1.1, 1.2);
  return Math.round(base * boost);
}

function elapsedBucketPlays(
  songId: string,
  dayIndex: number,
  dailyTotal: number,
  elapsedInDayMs: number
): number {
  const completedBuckets = Math.min(
    GROWTH_BUCKETS_PER_DAY,
    Math.floor(Math.max(0, elapsedInDayMs) / GROWTH_BUCKET_MS)
  );
  if (completedBuckets <= 0 || dailyTotal <= 0) return 0;
  if (completedBuckets >= GROWTH_BUCKETS_PER_DAY) return dailyTotal;

  let elapsedWeight = 0;
  let totalWeight = 0;
  for (let bucket = 0; bucket < GROWTH_BUCKETS_PER_DAY; bucket += 1) {
    const weight = integerInRange(`${songId}:day:${dayIndex}:bucket:${bucket}`, 1, 100);
    totalWeight += weight;
    if (bucket < completedBuckets) elapsedWeight += weight;
  }
  return Math.floor((dailyTotal * elapsedWeight) / totalWeight);
}

function validTimestamp(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Only the explicitly listed legacy admin tracks retain a pre-existing baseline. */
export function demoSongStatBaselineFor(
  song: Pick<Song, 'id' | 'creatorIsAdmin'>
): DemoSongStatOverride | undefined {
  if (song.creatorIsAdmin !== true) return undefined;
  return overrides[song.id];
}

/** Deterministic simulated growth. It never reads or writes genuine engagement records. */
export function simulatedSongGrowthFor(
  song: Pick<Song, 'id' | 'status' | 'visibility' | 'discoverableAt'>,
  at: number = Date.now()
): Required<Pick<DemoSongStatOverride, 'playCount' | 'loveCount' | 'saveCount' | 'shareCount'>> {
  const empty = { playCount: 0, loveCount: 0, saveCount: 0, shareCount: 0 };
  if (song.status !== 'ready' || song.visibility !== 'public') return empty;
  const startedAt = validTimestamp(song.discoverableAt) ?? SIMULATION_ROLLOUT_AT;
  const elapsed = Math.max(0, at - startedAt);
  if (elapsed < GROWTH_BUCKET_MS) return empty;

  const finalDay = Math.floor(elapsed / DAY_MS);
  const growth = { ...empty };
  const dayTypes = simulatedDayTypesFor(song.id, finalDay + 1);
  for (const [dayIndex, type] of dayTypes.entries()) {
    const dailyTotal = dailyPlayTotal(song.id, dayIndex, type);
    const elapsedInDay = Math.min(DAY_MS, elapsed - dayIndex * DAY_MS);
    const plays = elapsedBucketPlays(song.id, dayIndex, dailyTotal, elapsedInDay);
    growth.playCount += plays;
    growth.loveCount += Math.round(
      plays * ratioInRange(`${song.id}:day:${dayIndex}:loves`, 0.03, 0.065)
    );
    growth.saveCount += Math.round(
      plays * ratioInRange(`${song.id}:day:${dayIndex}:saves`, 0.015, 0.04)
    );
    growth.shareCount += Math.round(
      plays * ratioInRange(`${song.id}:day:${dayIndex}:shares`, 0.002, 0.008)
    );
  }
  return growth;
}

/** Legacy baseline plus elapsed growth, used only when demo stats are enabled. */
export function demoSongStatProjectionFor(
  song: Pick<Song, 'id' | 'creatorIsAdmin' | 'status' | 'visibility' | 'discoverableAt'>,
  at: number = Date.now()
): DemoSongStatOverride {
  const baseline = demoSongStatBaselineFor(song);
  const growth = simulatedSongGrowthFor(song, at);
  return {
    ...(baseline?.creatorName ? { creatorName: baseline.creatorName } : null),
    playCount: (baseline?.playCount ?? 0) + growth.playCount,
    loveCount: (baseline?.loveCount ?? 0) + growth.loveCount,
    saveCount: (baseline?.saveCount ?? 0) + growth.saveCount,
    shareCount: (baseline?.shareCount ?? 0) + growth.shareCount,
  };
}

export function demoSongStatOverrideFor(song: Song): DemoSongStatOverride | undefined {
  if (!demoSongStatsEnabled()) return undefined;
  if (song.status !== 'ready' || song.visibility !== 'public') return undefined;
  return demoSongStatProjectionFor(song);
}

export function applyDemoSongStatOverrides(song: Song): Song {
  const override = demoSongStatOverrideFor(song);
  if (!override?.creatorName || override.creatorName === song.creatorName) return song;

  return {
    ...song,
    // Counts stay real on the record. engagement.ts adds the demo baseline at read time.
    creatorName: override.creatorName,
  };
}

export function applyDemoSongStatOverridesToList<T extends Song>(songs: readonly T[]): T[] {
  return songs.map((song) => applyDemoSongStatOverrides(song) as T);
}
